import React, { useEffect, useRef, useState } from 'react';
import {
    createRunState, stepRun, jump, jumpRelease, duckPress, duckRelease,
    W, H, GROUND, type RunState,
} from './runLogic';
import { renderSprite, clearSpriteCache, S, CRANE_BG, CLOUD, DUCK, JUMP, RUN_A, RUN_B } from './sprites';
import { PIXEL_FONT, readHighScore, writeHighScore } from './runShared';

/**
 * "SiGeKo Run" — der Pixel-Runner auf der Wartungsseite. Zeichnet in ein
 * Canvas, laeuft in einer rAF-Schleife und haelt den Spielzustand in Refs;
 * React rendert nur das <canvas>-Element selbst.
 *
 * Die Logik lebt in runLogic.ts (testbar), hier nur Eingabe, Zeichnen, Ton.
 */
export interface SigekoRunProps {
    /** Tag/Nacht — der Wert kommt vom Spiel (alle 700 Punkte) und wird nach oben gemeldet. */
    night: boolean;
    onNightChange: (night: boolean) => void;
    /** Ton an/aus (Standard aus). Beim Einschalten piept es einmal, wie im Prototyp. */
    sound?: boolean;
    /** Haelt die Schleife an und ignoriert Eingaben (z. B. Dialog darueber). */
    paused?: boolean;
}

const INK_LIGHT = '#1B1F26';
const INK_DARK = '#E6E9EE';
const BG_LIGHT = '#F7F7F5';
const BG_DARK = '#1B1F26';

interface Beeper {
    beep(freq: number, dur: number, type?: OscillatorType): void;
    /** Ton an/aus; liefert den vorherigen Stand. */
    setOn(on: boolean): boolean;
    close(): void;
}

// 8-bit-Ton ueber WebAudio. Der AudioContext entsteht erst beim ersten Piep
// (Browser verlangen eine Nutzergeste) und lebt ausserhalb von React, damit
// keine Render-Funktion einen Ref beschreiben muss.
function createBeeper(): Beeper {
    let ac: AudioContext | null = null;
    let on = false;
    return {
        setOn(next) {
            const was = on;
            on = next;
            return was;
        },
        beep(freq, dur, type = 'square') {
            if (!on) return;
            try {
                const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
                if (!Ctor) return;
                ac = ac || new Ctor();
                const o = ac.createOscillator();
                const a = ac.createGain();
                o.type = type;
                o.frequency.value = freq;
                a.gain.value = 0.06;
                o.connect(a);
                a.connect(ac.destination);
                o.start();
                a.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + dur);
                o.stop(ac.currentTime + dur);
            } catch {
                // Kein WebAudio: das Spiel bleibt stumm.
            }
        },
        close() {
            ac?.close().catch(() => { /* schon zu */ });
            ac = null;
        },
    };
}

export const SigekoRun: React.FC<SigekoRunProps> = ({ night, onNightChange, sound = false, paused = false }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const stRef = useRef<RunState>(createRunState());
    const hiRef = useRef<number>(0);
    const pausedRef = useRef<boolean>(paused);
    const onNightChangeRef = useRef(onNightChange);
    const [beeper] = useState<Beeper>(createBeeper);

    // Props in Refs spiegeln, damit die rAF-Schleife und die Event-Handler
    // immer den aktuellen Wert sehen, ohne dass der Spiel-Effekt neu aufgesetzt wird.
    useEffect(() => {
        onNightChangeRef.current = onNightChange;
    }, [onNightChange]);
    useEffect(() => {
        pausedRef.current = paused;
    }, [paused]);

    // Ton einschalten bestaetigt sich mit einem kurzen Piep (Prototyp: Klick auf "TON: AN").
    useEffect(() => {
        const was = beeper.setOn(sound);
        if (sound && !was) beeper.beep(660, 0.08);
    }, [sound, beeper]);

    useEffect(() => {
        const cv = canvasRef.current;
        if (!cv) return;
        const g = cv.getContext('2d');
        if (!g) return;
        const st = stRef.current;
        hiRef.current = readHighScore();

        // Scharfe Pixel: K-fache Pixeldichte, dann setTransform statt CSS-Skalierung.
        function fit(): void {
            if (!cv || !g) return;
            const dpr = window.devicePixelRatio || 1;
            const K = Math.max(2, Math.ceil(cv.clientWidth * dpr / W));
            cv.width = W * K;
            cv.height = H * K;
            g.setTransform(K, 0, 0, K, 0, 0);
            g.imageSmoothingEnabled = false;
        }
        fit();
        window.addEventListener('resize', fit);
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
        ro?.observe(cv);
        if (document.fonts && document.fonts.load) {
            document.fonts.load('10px "Press Start 2P"').catch(() => { /* Schrift fehlt: Rueckfall */ });
        }

        // prefers-reduced-motion: die Welt steht ohnehin, bis der Nutzer bewusst
        // startet (stepRun bewegt nichts vor `started`). Wer Bewegung reduziert
        // haben will, bekommt deshalb bis zur Leertaste ein Standbild.
        const reduced = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

        const ink = (): string => (st.night ? INK_DARK : INK_LIGHT);

        function syncNight(): void {
            onNightChangeRef.current(st.night);
        }

        function die(): void {
            beeper.beep(160, 0.22, 'sawtooth');
            if (st.score > hiRef.current) {
                hiRef.current = st.score;
                writeHighScore(hiRef.current);
            }
        }

        function doJump(): void {
            if (pausedRef.current) return;
            const ev = jump(st);
            if (ev.restarted) {
                clearSpriteCache();
                syncNight();
            }
            if (ev.jumped) beeper.beep(520, 0.05);
        }

        function draw(): void {
            if (!g) return;
            const I = ink();
            g.imageSmoothingEnabled = false;
            g.clearRect(0, 0, W, H);
            g.fillStyle = st.night ? BG_DARK : BG_LIGHT;
            g.fillRect(0, 0, W, H);
            // Hintergrund
            g.globalAlpha = 0.55;
            g.drawImage(renderSprite(CRANE_BG, I), Math.round(st.craneX), GROUND - CRANE_BG.length * S + 2);
            for (const c of st.clouds) g.drawImage(renderSprite(CLOUD, I), Math.round(c.x), Math.round(c.y));
            g.globalAlpha = 1;
            // Boden
            g.fillStyle = I;
            g.fillRect(0, GROUND, W, 2);
            for (const d of st.groundDots) g.fillRect(Math.round(d.x), GROUND + 6 + (d.w > 4 ? 3 : 0), Math.round(d.w), 2);
            // Hindernisse
            for (const o of st.obs) {
                if (o.k === 'pit') {
                    g.fillStyle = st.night ? BG_DARK : BG_LIGHT;
                    g.fillRect(Math.round(o.x), GROUND, o.w, 4);
                    g.fillStyle = I;
                    g.fillRect(Math.round(o.x), GROUND + 4, o.w, 2);
                    // Absperrung links+rechts: gelb-schwarz
                    for (let k = 0; k < 2; k++) {
                        const px = Math.round(o.x) + (k ? o.w - 6 : 0);
                        g.fillStyle = '#F2C230';
                        g.fillRect(px, GROUND - 10, 6, 10);
                        g.fillStyle = I;
                        g.fillRect(px, GROUND - 7, 6, 3);
                    }
                    continue;
                }
                if (!o.rows) continue;
                const s = renderSprite(o.rows, I);
                for (let n = 0; n < (o.n || 1); n++) g.drawImage(s, Math.round(o.x) + n * s.width, Math.round(o.y));
                if (o.k === 'hook') {
                    g.fillStyle = '#8A93A0';
                    g.fillRect(Math.round(o.x) + 9 * S, 0, S * 2, Math.max(0, Math.round(o.y)));
                }
            }
            // Laeufer
            let sp = st.duck && st.onGround
                ? renderSprite(DUCK, I)
                : (!st.onGround ? renderSprite(JUMP, I) : (Math.floor(st.frame) % 2 ? renderSprite(RUN_A, I) : renderSprite(RUN_B, I)));
            if (!st.started && st.onGround) sp = renderSprite(JUMP, I);
            g.drawImage(sp, st.x, (st.duck && st.onGround ? GROUND - sp.height : st.y - sp.height) + 2);
            // Punkte
            g.fillStyle = I;
            g.font = `10px ${PIXEL_FONT}`;
            g.textAlign = 'right';
            let scoreTxt = String(st.score).padStart(5, '0');
            if (st.flash > 0 && Math.floor(st.t * 8) % 2 === 0) scoreTxt = '     ';
            const hi = hiRef.current;
            g.fillText((hi ? 'HI ' + String(hi).padStart(5, '0') + '  ' : '') + scoreTxt, W - 10, 20);
            g.textAlign = 'center';
            if (!st.started) {
                g.fillText('LEERTASTE ODER TIPPEN', W / 2, 60);
                g.font = `8px ${PIXEL_FONT}`;
                g.fillText('SIGEKO RUN', W / 2, 42);
            }
            if (st.over) {
                g.font = `12px ${PIXEL_FONT}`;
                g.fillText('FEIERABEND', W / 2, 56);
                // Neustart-Symbol
                const cx = W / 2, cy = 84;
                g.strokeStyle = I;
                g.lineWidth = 3;
                g.beginPath();
                g.arc(cx, cy, 10, -0.4, 4.6);
                g.stroke();
                g.fillStyle = I;
                g.beginPath();
                g.moveTo(cx + 11, cy - 9);
                g.lineTo(cx + 3, cy - 8);
                g.lineTo(cx + 10, cy - 1);
                g.fill();
                g.font = `8px ${PIXEL_FONT}`;
                g.fillText(st.score >= hi && st.score > 0 ? 'NEUER REKORD' : 'NOCH MAL: LEERTASTE', W / 2, 112);
            }
            g.textAlign = 'left';
        }

        let raf: number | null = null;
        let last = performance.now();
        function loop(now: number): void {
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now;
            if (!pausedRef.current) {
                const ev = stepRun(st, dt);
                if (ev.milestone) {
                    beeper.beep(880, 0.06);
                    setTimeout(() => beeper.beep(1175, 0.08), 70);
                }
                if (ev.nightToggled) {
                    clearSpriteCache();
                    syncNight();
                }
                if (ev.died) die();
            }
            draw();
            raf = requestAnimationFrame(loop);
        }

        // Eingabe
        let touchY: number | null = null;
        const onKeyDown = (e: KeyboardEvent): void => {
            if (e.code === 'Space' || e.code === 'ArrowUp') {
                e.preventDefault();
                if (!e.repeat) doJump();
            }
            if (e.code === 'ArrowDown') {
                e.preventDefault();
                if (!pausedRef.current) duckPress(st);
            }
        };
        const onKeyUp = (e: KeyboardEvent): void => {
            if (e.code === 'ArrowDown') duckRelease(st);
            if (e.code === 'Space' || e.code === 'ArrowUp') jumpRelease(st);
        };
        const onPointerDown = (e: PointerEvent): void => {
            e.preventDefault();
            touchY = e.clientY;
            doJump();
        };
        const onPointerMove = (e: PointerEvent): void => {
            // Finger nach unten ziehen = ducken (ohne den Fall zu beschleunigen, wie im Prototyp).
            if (touchY !== null && e.clientY - touchY > 18) duckPress(st, false);
        };
        const onPointerUp = (): void => {
            touchY = null;
            duckRelease(st);
            jumpRelease(st);
        };
        const onPointerCancel = (): void => {
            touchY = null;
            duckRelease(st);
        };
        // Tab-Wechsel beendet die Runde: die Welt liefe sonst ungesehen weiter.
        const onVisibility = (): void => {
            if (document.hidden && st.started && !st.over) {
                st.over = true;
                die();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        window.addEventListener('keyup', onKeyUp);
        cv.addEventListener('pointerdown', onPointerDown);
        cv.addEventListener('pointermove', onPointerMove);
        cv.addEventListener('pointerup', onPointerUp);
        cv.addEventListener('pointercancel', onPointerCancel);
        document.addEventListener('visibilitychange', onVisibility);

        // Mit reduzierter Bewegung erst einmal nur ein Standbild; die Schleife
        // startet mit dem ersten bewussten Sprung. Sonst laeuft sie sofort.
        const startLoop = (): void => {
            if (raf === null) {
                last = performance.now();
                raf = requestAnimationFrame(loop);
            }
            window.removeEventListener('keydown', startLoop);
            cv.removeEventListener('pointerdown', startLoop);
        };
        if (reduced) {
            draw();
            window.addEventListener('keydown', startLoop);
            cv.addEventListener('pointerdown', startLoop);
        } else {
            raf = requestAnimationFrame(loop);
        }

        return () => {
            if (raf !== null) cancelAnimationFrame(raf);
            window.removeEventListener('keydown', startLoop);
            cv.removeEventListener('pointerdown', startLoop);
            window.removeEventListener('resize', fit);
            ro?.disconnect();
            window.removeEventListener('keydown', onKeyDown);
            window.removeEventListener('keyup', onKeyUp);
            cv.removeEventListener('pointerdown', onPointerDown);
            cv.removeEventListener('pointermove', onPointerMove);
            cv.removeEventListener('pointerup', onPointerUp);
            cv.removeEventListener('pointercancel', onPointerCancel);
            document.removeEventListener('visibilitychange', onVisibility);
            beeper.close();
        };
    }, [beeper]);

    // Die Nacht des Spiels gewinnt: kippt der Elternteil den Wert von aussen
    // (z. B. Neustart der Seite), zieht das Spiel nach, sonst passt die Tinte nicht.
    useEffect(() => {
        if (stRef.current.night !== night) {
            stRef.current.night = night;
            clearSpriteCache();
        }
    }, [night]);

    return (
        <canvas
            ref={canvasRef}
            width={W}
            height={H}
            tabIndex={0}
            aria-label="SiGeKo Run"
            className="block w-full h-auto outline-none"
            style={{ touchAction: 'manipulation' }}
        />
    );
};

export default SigekoRun;
